import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import * as d3 from 'd3';
import { useDispatch, useSelector } from 'react-redux';
import { CURRENT_MONITORED_URI_CHART_FILTERS_SET, DEFAULT_HIDDEN_SERIES } from '../../reducers/currentMonitoredUri';
import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import HistoryIcon from '@material-ui/icons/History';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { formatDateOnly, getDateAtIndex, getIndexForDate, isSameDay, usePushParams, parseCrawlDate } from './commons';
import { CountBadge } from './Pills';
import { DESTINATION_PERF_TAG, SOURCE_PERF_TAG } from './index';
import { LABEL } from './Diff';
import { finalHttpStatus } from './redirectChainStatus';

import ChartTooltip from './ChartTooltip';
import ChartLegend from './ChartLegend';
import ChangeTypeFilter from './ChangeTypeFilter';
import { SegmentedRow, SegmentedButton } from "../common/SegmentedButtons";
import SourceToggles from "../common/SourceToggles";
import analyticsLogo from '../AccountDetails/analytics-logo.png';
import searchConsoleLogo from '../WorkspaceAdmin/SearchConsoleSettings/search-console-logo.svg';

const MS_DAY = 24 * 3600 * 1000;
const range = num => [...Array(num).keys()];
const dayKey = d => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

const ALL_PRESETS = [
    { label: '1m', days: 30 },
    { label: '3m', days: 90 },
    { label: '6m', days: 180 },
    { label: '1y', days: 365 },
    { label: '2y', days: 365 * 2 },
    { label: '3y', days: 365 * 3 },
    { label: '5y', days: 365 * 5 },
];

/** The Search Console figures the chart can draw, each toggled on its own once the source is linked. */
const SEARCH_METRICS = [
    { key: 'clicks', label: 'Clicks' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'position', label: 'Position' },
];

const getSessionForTag = (pagePerformances, curDate, tag) => {
    const perf = (pagePerformances || []).find(p => p.tag === tag);
    if (!perf) return 0;
    const dayPerf = perf.data.find(p => isSameDay(new Date(p.timestamp), curDate));
    return dayPerf ? dayPerf.sessions : 0;
};

const getSearchDay = (pageSearchPerformances, curDate) =>
    (pageSearchPerformances || []).find(p => isSameDay(new Date(p.timestamp), curDate));

const hasTagWithData = (pagePerformances, tag) =>
    (pagePerformances || []).some(p => p.tag === tag && Array.isArray(p.data) && p.data.length > 0);

const buildChangesIndex = pageChanges => {
    const idx = new Map();
    (pageChanges || []).forEach(pc => {
        const parsed = parseCrawlDate(pc.crawlDate);
        const k = dayKey(parsed);
        const flags = idx.get(k) || {};
        pc.changes.forEach(c => { flags[c.key] = true; });
        // Store the UTC date so the ?date= param matches the archive's UTC-based day boundaries.
        flags._utcCrawlDate = formatDateOnly(parsed);
        const redirectChange = pc.changes.find(c => c.key === 'redirectChainElements');
        if (redirectChange) {
            const status = finalHttpStatus(redirectChange.newValue);
            if (status !== null) {
                flags._httpStatus = status;
            } else if (Array.isArray(redirectChange.newValue) && redirectChange.newValue.length === 0) {
                flags._crawlFailed = true;
            }
        }
        idx.set(k, flags);
    });
    return idx;
};

const buildTimelineData = (pagePerformances, pageSearchPerformances, pageChanges, numDaysHistory, curSnapshotDate, activeTypes) => {
    const changesIdx = buildChangesIndex(pageChanges);
    const filterKeys = activeTypes?.length > 0 ? activeTypes : Object.keys(LABEL);
    return range(numDaysHistory).map(index => {
        const curDate = getDateAtIndex(index);
        const flags = changesIdx.get(dayKey(curDate)) || {};
        const selected = curSnapshotDate ? flags._utcCrawlDate === curSnapshotDate : false;
        const searchDay = getSearchDay(pageSearchPerformances, curDate);
        return {
            date: curDate.getTime(),
            dateObj: curDate,
            sessionsAtSrc: getSessionForTag(pagePerformances, curDate, SOURCE_PERF_TAG),
            sessionsAtDst: getSessionForTag(pagePerformances, curDate, DESTINATION_PERF_TAG),
            position: searchDay ? searchDay.position : null,
            clicks: searchDay ? searchDay.clicks : null,
            impressions: searchDay ? searchDay.impressions : null,
            selected,
            _hasFilteredChange: filterKeys.some(k => flags[k]),
            ...flags,
        };
    });
};

const CustomXTick = ({ x, y, payload, onSelect, selectedDate, formatTick }) => {
    if (payload == null) return null;
    const label = formatTick(payload.value);
    const date = formatDateOnly(new Date(payload.value));
    const isSelected = date === selectedDate;
    return (
        <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} onClick={() => onSelect(date)}>
            <text x={0} y={0} dy={12} textAnchor="middle"
                fill={isSelected ? '#6366f1' : '#94a3b8'}
                fontSize={11} fontWeight={isSelected ? 700 : 400}>
                {label}
            </text>
            {isSelected && <line x1={0} x2={0} y1={2} y2={8} stroke="#6366f1" strokeWidth={2} />}
        </g>
    );
};

const isErrorDay = point => point._crawlFailed || (point._httpStatus && parseInt(point._httpStatus, 10) >= 300);

const CustomizedDot = ({ cx, cy, payload, onClick }) => {
    if (cx == null || cy == null || !payload) return null;
    const isError = isErrorDay(payload);
    const color = isError ? '#dc2626' : '#6366f1';
    const showDot = payload._hasFilteredChange || isError;

    if (payload.selected) {
        return (
            <g onClick={onClick} style={{ cursor: 'pointer' }}>
                <circle cx={cx} cy={cy} r={20} fill="transparent" />
                <circle cx={cx} cy={cy} r={13} fill="none" stroke={color} strokeWidth={1.5} opacity={0.3} />
                <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} />
            </g>
        );
    }
    if (showDot) {
        return (
            <g onClick={onClick} style={{ cursor: 'pointer' }}>
                <circle cx={cx} cy={cy} r={20} fill="transparent" />
                <circle cx={cx} cy={cy} r={7} fill={color} stroke="#fff" strokeWidth={2} />
            </g>
        );
    }
    return null;
};

const Timeline = ({ onOpenDrawer }) => {
    const location = useLocation();
    const pushParams = usePushParams();
    const dispatch = useDispatch();
    const { numDaysHistory, pagePerformances, pageSearchPerformances, pageChanges, curSnapshotDate, chartFilters } = useSelector(
        s => s.currentMonitoredUri
    );
    const { hiddenSeries: hidden, changeTypes: activeTypes } = chartFilters;
    const { workspaceNumber } = useParams();
    const workspace = useSelector(s => s.workspaces.data.find(ws => String(ws.seqNumber) === workspaceNumber));
    const connect = {
        analytics: workspace && !workspace.analyticsSettings?.tokenName ? `/ws/${workspaceNumber}/admin?selectAnalyticsViewOpen=true` : null,
        searchConsole: workspace && !workspace.searchConsoleSettings?.siteUrl ? `/ws/${workspaceNumber}/admin?selectSearchConsoleOpen=true` : null,
    };


    const { viewStart, viewEnd } = useMemo(() => {
        const p = new URLSearchParams(location.search);
        return { viewStart: p.get('from') || null, viewEnd: p.get('to') || null };
    }, [location.search]);

    const zoomed = viewStart !== null || viewEnd !== null;

    const hasAnyFilter = useMemo(() => {
        const p = new URLSearchParams(location.search);
        const hiddenAsDefault = hidden.length === DEFAULT_HIDDEN_SERIES.length && DEFAULT_HIDDEN_SERIES.every(k => hidden.includes(k));
        return p.has('date') || p.has('from') || p.has('to') || !hiddenAsDefault || activeTypes.length > 0;
    }, [location.search, hidden, activeTypes]);

    const resetView = useCallback(() => {
        pushParams({ date: null, from: null, to: null });
        dispatch({ type: CURRENT_MONITORED_URI_CHART_FILTERS_SET, payload: { hiddenSeries: DEFAULT_HIDDEN_SERIES, changeTypes: [] } });
    }, [pushParams, dispatch]);

    const toggleSeries = useCallback(key => {
        const next = hidden.includes(key) ? hidden.filter(k => k !== key) : [...hidden, key];
        dispatch({ type: CURRENT_MONITORED_URI_CHART_FILTERS_SET, payload: { hiddenSeries: next } });
    }, [hidden, dispatch]);

    const presets = useMemo(() => [
        ...ALL_PRESETS.filter(p => p.days < numDaysHistory),
        { label: 'Max', days: null },
    ], [numDaysHistory]);

    const activePreset = useMemo(() => {
        if (!viewStart && !viewEnd) return 'Max';
        const todayStr = formatDateOnly(getDateAtIndex(0));
        for (const p of presets) {
            if (!p.days) continue;
            if (viewStart === formatDateOnly(getDateAtIndex(p.days - 1)) && viewEnd === todayStr) return p.label;
        }
        return null;
    }, [viewStart, viewEnd, presets]);

    const dragRef = useRef(null);
    const [dragFrom, setDragFrom] = useState(null);
    const [dragTo,   setDragTo]   = useState(null);

    const tickFormatter = useMemo(() => {
        const f = d3.timeFormat('%d/%m');
        return v => f(new Date(v));
    }, []);

    const hasDst = useMemo(() => hasTagWithData(pagePerformances, DESTINATION_PERF_TAG), [pagePerformances]);
    const hasSearch = (pageSearchPerformances || []).length > 0;
    const showAnalytics = !connect.analytics && !hidden.includes('analytics');
    const searchShown = SEARCH_METRICS.filter(({ key }) => !connect.searchConsole && !hidden.includes(key)).map(({ key }) => key);
    const drawn = key => hasSearch && searchShown.includes(key);
    const showChanges = !hidden.includes('changes');
    const changeDot = showChanges ? <CustomizedDot /> : false;

    const toggleType = useCallback(key => {
        const next = activeTypes.includes(key)
            ? activeTypes.filter(k => k !== key)
            : [...activeTypes, key];
        dispatch({ type: CURRENT_MONITORED_URI_CHART_FILTERS_SET, payload: { changeTypes: next } });
    }, [activeTypes, dispatch]);

    const allData = useMemo(
        () => buildTimelineData(pagePerformances, pageSearchPerformances, pageChanges, numDaysHistory, curSnapshotDate, activeTypes),
        [pagePerformances, pageSearchPerformances, pageChanges, numDaysHistory, curSnapshotDate, activeTypes]
    );

    const data = useMemo(() => {
        if (!viewStart && !viewEnd) return allData;
        return allData.filter(d => {
            const ds = formatDateOnly(new Date(d.date));
            if (viewStart && ds < viewStart) return false;
            if (viewEnd   && ds > viewEnd)   return false;
            return true;
        });
    }, [allData, viewStart, viewEnd]);

    const selectSnapshot = useCallback(
        date => pushParams({ date }),
        [pushParams]
    );

    const applyPreset = useCallback(preset => {
        if (!preset.days) {
            pushParams({ from: null, to: null });
        } else {
            pushParams({
                from: formatDateOnly(getDateAtIndex(preset.days - 1)),
                to:   formatDateOnly(getDateAtIndex(0)),
            });
        }
    }, [pushParams]);

    const resetZoom = useCallback(() => {
        pushParams({ from: null, to: null });
    }, [pushParams]);

    const zoomIn = useCallback(() => {
        if (!viewStart || !viewEnd) return;
        const start = new Date(viewStart).getTime();
        const end   = new Date(viewEnd).getTime();
        const center = (start + end) / 2;
        const newHalf = (end - start) / 4;
        pushParams({
            from: formatDateOnly(new Date(center - newHalf)),
            to:   formatDateOnly(new Date(center + newHalf)),
        });
    }, [viewStart, viewEnd, pushParams]);

    const zoomOut = useCallback(() => {
        const minDate = getDateAtIndex(numDaysHistory - 1).getTime();
        const maxDate = getDateAtIndex(0).getTime();
        const start = viewStart ? new Date(viewStart).getTime() : minDate;
        const end   = viewEnd   ? new Date(viewEnd).getTime()   : maxDate;
        const center = (start + end) / 2;
        const newHalf = (end - start);
        const newStart = Math.max(center - newHalf, minDate);
        const newEnd   = Math.min(center + newHalf, maxDate);
        if (newStart <= minDate && newEnd >= maxDate) {
            pushParams({ from: null, to: null });
        } else {
            pushParams({ from: formatDateOnly(new Date(newStart)), to: formatDateOnly(new Date(newEnd)) });
        }
    }, [viewStart, viewEnd, numDaysHistory, pushParams]);

    const onMouseDown = useCallback(e => {
        if (!e?.activeLabel) return;
        dragRef.current = { from: e.activeLabel };
        setDragFrom(e.activeLabel);
        setDragTo(null);
    }, []);

    const onMouseMove = useCallback(e => {
        if (!dragRef.current || !e?.activeLabel) return;
        dragRef.current.to = e.activeLabel;
        setDragTo(e.activeLabel);
    }, []);

    const onMouseUp = useCallback(e => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag) return;

        const from = drag.from;
        if (from != null && dragTo != null && Math.abs(dragTo - from) > MS_DAY * 2) {
            pushParams({
                from: formatDateOnly(new Date(Math.min(from, dragTo))),
                to:   formatDateOnly(new Date(Math.max(from, dragTo))),
            });
        } else if (e?.activePayload?.[0]?.payload?.dateObj) {
            const payload = e.activePayload[0].payload;
            selectSnapshot(payload._utcCrawlDate || formatDateOnly(payload.dateObj));
        }

        setDragFrom(null);
        setDragTo(null);
    }, [dragTo, selectSnapshot, pushParams]);

    useEffect(() => {
        function onGlobalMouseUp() {
            if (!dragRef.current) return;
            const { from, to } = dragRef.current;
            dragRef.current = null;
            setDragFrom(null);
            setDragTo(null);
            if (from != null && to != null && Math.abs(to - from) > MS_DAY * 2) {
                pushParams({
                    from: formatDateOnly(new Date(Math.min(from, to))),
                    to:   formatDateOnly(new Date(Math.max(from, to))),
                });
            }
        }
        document.addEventListener('mouseup', onGlobalMouseUp);
        return () => document.removeEventListener('mouseup', onGlobalMouseUp);
    }, [pushParams]);

    const changeCount = useMemo(() => {
        const windowStartDate = formatDateOnly(getDateAtIndex(numDaysHistory - 1));
        return (pageChanges || []).filter(pc => {
            if (!pc.changes?.length) return false;
            const ds = formatDateOnly(parseCrawlDate(pc.crawlDate));
            if (ds < windowStartDate) return false;
            if (viewStart && ds < viewStart) return false;
            if (viewEnd   && ds > viewEnd)   return false;
            return true;
        }).length;
    }, [pageChanges, viewStart, viewEnd, numDaysHistory]);

    const selectedTs = useMemo(() => {
        if (!curSnapshotDate) return null;
        const pt = data.find(d => formatDateOnly(new Date(d.date)) === curSnapshotDate);
        return pt ? pt.date : null;
    }, [data, curSnapshotDate]);

    const isDragging = dragFrom !== null && dragTo !== null;

    return (
        <Wrap>
            <ChartHeader>
                <SegmentedRow>
                    {presets.map(p => (
                        <SegmentedButton
                            key={p.label}
                            $active={activePreset === p.label}
                            onClick={() => applyPreset(p)}
                        >
                            {p.label}
                        </SegmentedButton>
                    ))}
                    {zoomed && activePreset === null && (
                        <SegmentedButton $active onClick={resetZoom} title="Reset zoom">
                            <ZoomOutIcon style={{ fontSize: '0.85rem' }} />
                        </SegmentedButton>
                    )}
                </SegmentedRow>
                {zoomed && (
                    <ZoomControls>
                        <ZoomBtn onClick={zoomOut} title="Zoom out"><ZoomOutIcon style={{ fontSize: '1.1rem' }} /> Zoom out</ZoomBtn>
                        <ZoomBtn onClick={zoomIn}  title="Zoom in"><ZoomInIcon  style={{ fontSize: '1.1rem' }} /> Zoom in</ZoomBtn>
                    </ZoomControls>
                )}
                <RightControls>
                    {hasAnyFilter && <ResetViewBtn onClick={resetView}>Reset view</ResetViewBtn>}
                    {connect.analytics ? (
                        <SegmentedRow>
                            <ConnectButton as={Link} to={connect.analytics} title="Connect Google Analytics">
                                + <SeriesIcon src={analyticsLogo} alt="" />Analytics
                            </ConnectButton>
                        </SegmentedRow>
                    ) : (
                        <SourceToggles icon={analyticsLogo} source="Analytics" onToggle={toggleSeries}
                                       toggles={[{ key: 'analytics', label: 'Sessions', on: !hidden.includes('analytics') }]} />
                    )}
                    {connect.searchConsole ? (
                        <SegmentedRow>
                            <ConnectButton as={Link} to={connect.searchConsole} title="Connect Google Search Console">
                                + <SeriesIcon src={searchConsoleLogo} alt="" />Search Console
                            </ConnectButton>
                        </SegmentedRow>
                    ) : (
                        <SourceToggles icon={searchConsoleLogo} source="Search Console" onToggle={toggleSeries}
                                       toggles={SEARCH_METRICS.map(({ key, label }) => ({ key, label, on: !hidden.includes(key) }))} />
                    )}
                    <SegmentedRow>
                        <SegmentedButton $active={!hidden.includes('changes')} aria-pressed={!hidden.includes('changes')} onClick={() => toggleSeries('changes')}>
                            Page changes
                        </SegmentedButton>
                    </SegmentedRow>
                    <ChangeTypeFilter
                        activeTypes={activeTypes}
                        onToggle={toggleType}
                        onClear={() => dispatch({ type: CURRENT_MONITORED_URI_CHART_FILTERS_SET, payload: { changeTypes: [] } })}
                    />
                    <ChangelogBtn onClick={onOpenDrawer}>
                        <HistoryIcon style={{ fontSize: '0.95rem' }} />
                        Change log
                        {changeCount > 0 && <CountBadge>{changeCount}</CountBadge>}
                    </ChangelogBtn>
                </RightControls>
            </ChartHeader>
            <ChartArea>
                <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart
                        data={data}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        style={{ cursor: isDragging ? 'col-resize' : 'pointer' }}
                        onClick={undefined}
                    >
                        <defs>
                            <linearGradient id="srcGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.18} />
                                <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="dstGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.18} />
                                <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                            reversed
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={<CustomXTick onSelect={selectSnapshot} selectedDate={curSnapshotDate} formatTick={tickFormatter} />}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            width={36}
                            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v}
                        />
                        {drawn('impressions') && <YAxis yAxisId="impressions" hide domain={[0, 'auto']} />}
                        {drawn('position') && (
                            <YAxis
                                yAxisId="search"
                                orientation="right"
                                axisLine={false}
                                tickLine={false}
                                reversed
                                domain={[1, 'dataMax']}
                                allowDecimals={false}
                                tick={{ fontSize: 10, fill: '#fdba74' }}
                                width={36}
                            />
                        )}
                        <Tooltip content={<ChartTooltip showPosition={drawn('position')} />} />
                        {selectedTs != null && !isDragging && (
                            <ReferenceLine
                                x={selectedTs}
                                stroke="#6366f1"
                                strokeWidth={1.5}
                                strokeDasharray="4 3"
                                strokeOpacity={0.5}
                            />
                        )}
                        {isDragging && (
                            <ReferenceArea
                                x1={Math.min(dragFrom, dragTo)}
                                x2={Math.max(dragFrom, dragTo)}
                                fill="#6366f1"
                                fillOpacity={0.15}
                                stroke="#6366f1"
                                strokeOpacity={0.6}
                                strokeWidth={1}
                            />
                        )}
                        {showAnalytics && hasDst && (
                            <Area type="monotone" dataKey="sessionsAtDst"
                                stroke="#5eead4" strokeWidth={1} fill="url(#dstGrad)"
                                dot={changeDot} isAnimationActive={false} />
                        )}
                        {showAnalytics && (
                            <Area type="monotone" dataKey="sessionsAtSrc"
                                stroke="#a5b4fc" strokeWidth={1} fill="url(#srcGrad)"
                                dot={hasDst ? false : changeDot} isAnimationActive={false} />
                        )}
                        {!showAnalytics && showChanges && (
                            <Line dataKey={() => 0} stroke="none" dot={changeDot} activeDot={false} isAnimationActive={false} />
                        )}
                        {drawn('clicks') && (
                            <Line type="monotone" dataKey="clicks" stroke="#38bdf8" strokeWidth={1}
                                dot={false} isAnimationActive={false} connectNulls />
                        )}
                        {drawn('impressions') && (
                            <Line type="monotone" dataKey="impressions" yAxisId="impressions" stroke="#14b8a6" strokeWidth={1}
                                dot={false} isAnimationActive={false} connectNulls />
                        )}
                        {drawn('position') && (
                            <Line type="monotone" dataKey="position" yAxisId="search" stroke="#f97316" strokeWidth={2}
                                dot={false} isAnimationActive={false} connectNulls />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </ChartArea>
            <ChartLegend
                hasSrc={showAnalytics}
                hasChangeDots={showChanges && data.some(point => point._hasFilteredChange && !isErrorDay(point))}
                hasErrorDots={showChanges && data.some(isErrorDay)}
                hasDst={showAnalytics && hasDst}
                searchShown={searchShown}
                searchEmpty={!hasSearch}
            />
        </Wrap>
    );
};

const Wrap = styled.div`
    padding: 0 16px 8px;
`;

const ChartHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    gap: 8px;
    flex-wrap: wrap;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    margin: 0 -16px;
`;

/** On a white backing so the logo keeps its colours on the filled, active button. */
const SeriesIcon = styled.img`
    width: 14px;
    height: 14px;
    padding: 1px;
    margin-right: 5px;
    border-radius: 3px;
    background: #fff;
`;

/** A Google source the workspace has not linked: the button opens the admin page to link it. */
const ConnectButton = styled(SegmentedButton)`
    border: 1px dashed #a5b4fc;
    color: #6366f1;
    text-decoration: none;
    &:hover { text-decoration: none; }
`;

const ZoomControls = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ZoomBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
    &:hover { background: #eef2ff; border-color: #a5b4fc; color: #6366f1; }
`;

const RightControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const ResetViewBtn = styled.button`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #fdba74;
    background: #fff7ed;
    color: #c2410c;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all 120ms;
    &:hover { background: #fed7aa; border-color: #fb923c; }
`;

const ChangelogBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 120ms;
    &:hover { background: #eef2ff; border-color: #a5b4fc; color: #6366f1; }
`;


const ChartArea = styled.div`
    min-width: 0;
    user-select: none;
    margin-top: 12px;
`;

export default Timeline;
